require('./bootstrap');

(() => {
    const TEXT_INPUT_TYPES = new Set([
        '',
        'email',
        'number',
        'search',
        'tel',
        'text',
        'url',
    ]);
    const CLEARABLE_FILTER_INPUT_TYPES = new Set([
        ...TEXT_INPUT_TYPES,
        'date',
        'datetime-local',
        'month',
        'time',
        'week',
    ]);
    const IGNORED_INPUT_TYPES = new Set([
        'button',
        'file',
        'image',
        'reset',
        'submit',
    ]);
    const TEXT_FILTER_DELAY = 550;
    const FILTER_RESET_SELECTOR = '[data-filter-reset-target]';
    let generatedFilterControlId = 0;

    const normalizeButtonText = (button) => (
        button.value || button.textContent || ''
    ).trim().toLowerCase();

    const isGetForm = (form) => (form.getAttribute('method') || 'get').toLowerCase() === 'get';

    const hasFilterSubmit = (form) => Array.from(
        form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])')
    ).some((button) => normalizeButtonText(button).includes('filtra'));

    const hasFilterAction = (form) => Array.from(
        form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])')
    ).some((button) => {
        const text = normalizeButtonText(button);

        return text.includes('filtra')
            || text.includes('filtri')
            || text.includes('aggiorna destinatari');
    });

    const isSupportedControl = (control) => {
        if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) {
            return false;
        }

        if (control.disabled || control.matches('[data-auto-filter-ignore]')) {
            return false;
        }

        return !(control instanceof HTMLInputElement && IGNORED_INPUT_TYPES.has(control.type));
    };

    const isTextFilterControl = (control) => (
        control instanceof HTMLTextAreaElement
        || (control instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(control.type))
    );

    const isSuggesterControl = (control) => (
        control instanceof HTMLInputElement
        && control.matches('[data-filter-suggester], [data-auto-filter-suggester], [data-driver-search-input]')
    );

    const selectedSuggesterTargets = (control) => {
        const targetIds = (
            control.dataset.autoFilterSuggesterTarget
            || control.dataset.filterSuggesterTarget
            || ''
        ).split(/\s+/).filter(Boolean);

        if (targetIds.length) {
            return targetIds.map((id) => document.getElementById(id)).filter(Boolean);
        }

        const driverSearch = control.closest('[data-driver-search]');
        const driverHidden = driverSearch ? driverSearch.querySelector('[data-driver-search-hidden]') : null;

        return driverHidden ? [driverHidden] : [];
    };

    const hasSelectedSuggesterValue = (control) => {
        const targets = selectedSuggesterTargets(control);

        if (!targets.length) {
            return String(control.value || '').trim() !== '';
        }

        return targets.every((target) => String(target.value || '').trim() !== '');
    };

    const shouldSubmitWhileTyping = (control) => (
        Boolean(control.name)
        || control.dataset.autoFilterInput === 'on'
    );

    const textFilterDelayFor = (control, form) => {
        const configuredDelay = Number.parseInt(
            control.dataset.autoFilterDelay || form.dataset.autoFilterDelay || '',
            10
        );

        return Number.isFinite(configuredDelay) && configuredDelay >= 0
            ? configuredDelay
            : TEXT_FILTER_DELAY;
    };

    const isOptionTrigger = (element) => {
        if (!(element instanceof Element)) {
            return false;
        }

        return Boolean(element.closest('[data-auto-filter-trigger], [role="option"]'));
    };

    const isComboboxTrigger = (element) => {
        if (!(element instanceof Element)) {
            return false;
        }

        return Boolean(element.closest('[role="combobox"]'));
    };

    const submitFilterForm = (form) => {
        if (form.dataset.autoFilterSubmitting === '1') {
            return;
        }

        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
            return;
        }

        form.dataset.autoFilterSubmitting = '1';
        form.setAttribute('aria-busy', 'true');
        form.submit();
    };

    const cssEscape = (value) => {
        if (window.CSS && typeof window.CSS.escape === 'function') {
            return window.CSS.escape(value);
        }

        return String(value).replace(/[^A-Za-z0-9_-]/g, '\\$&');
    };

    const ensureControlId = (control) => {
        if (control.id) {
            return control.id;
        }

        generatedFilterControlId += 1;
        control.id = `admin_filter_control_${generatedFilterControlId}`;

        return control.id;
    };

    const resolveResetControl = (form, reference) => {
        if (!reference) {
            return null;
        }

        const byId = document.getElementById(reference);
        if (byId && (!form || form.contains(byId))) {
            return byId;
        }

        if (!form || !form.elements) {
            return null;
        }

        const byName = form.elements[reference];
        if (byName instanceof Element) {
            return byName;
        }

        if (byName && byName.length) {
            return Array.from(byName).find((control) => control instanceof Element) || null;
        }

        return null;
    };

    const fieldContainerFor = (control) => (
        control.closest('[data-driver-search], .mb-3, .mb-4, .form-group, .col, [class*="col-"]')
        || control.parentElement
    );

    const relatedResetControlsFor = (control, form) => {
        const explicitReferences = (control.dataset.filterResetRelated || '')
            .split(/\s+/)
            .filter(Boolean);
        const explicitControls = explicitReferences
            .map((reference) => resolveResetControl(form, reference))
            .filter(Boolean);

        if (explicitControls.length) {
            return explicitControls;
        }

        const driverSearch = control.closest('[data-driver-search]');
        const driverHidden = driverSearch?.querySelector('[data-driver-search-hidden]');
        if (driverHidden && driverHidden !== control) {
            return [driverHidden];
        }

        const container = fieldContainerFor(control);
        if (!container) {
            return [];
        }

        const hiddenControls = Array.from(container.querySelectorAll('input[type="hidden"][name]'))
            .filter((hiddenControl) => hiddenControl !== control);

        if (!hiddenControls.length) {
            return [];
        }

        if (!control.name) {
            return hiddenControls;
        }

        return hiddenControls.filter((hiddenControl) => (
            hiddenControl.name.startsWith(`${control.name}_`)
            || (control.id && hiddenControl.id.startsWith(`${control.id}_`))
        ));
    };

    const controlsForResetButton = (button) => {
        const form = button.form || button.closest('form');
        const references = [
            button.dataset.filterResetTarget || '',
            ...(button.dataset.filterResetRelated || '').split(/\s+/),
        ].filter(Boolean);

        return references
            .map((reference) => resolveResetControl(form, reference))
            .filter(Boolean);
    };

    const hasControlValue = (control) => {
        if (control instanceof HTMLSelectElement && control.multiple) {
            return Array.from(control.selectedOptions).some((option) => option.value !== '');
        }

        return String(control.value || '').trim() !== '';
    };

    const updateResetButton = (button) => {
        const controls = controlsForResetButton(button);
        const hasValue = controls.some(hasControlValue);

        button.disabled = !hasValue;
        button.classList.toggle('disabled', !hasValue);
    };

    const dispatchControlResetEvents = (control) => {
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const clearResetControl = (control) => {
        if (control instanceof HTMLSelectElement) {
            Array.from(control.options).forEach((option) => {
                option.selected = false;
            });

            if (!control.multiple) {
                control.selectedIndex = 0;
            }
        } else {
            control.value = '';
        }

        dispatchControlResetEvents(control);
    };

    const labelForControl = (control) => {
        const label = control.id
            ? document.querySelector(`label[for="${cssEscape(control.id)}"]`)
            : null;

        return (label?.textContent || control.getAttribute('aria-label') || control.placeholder || 'campo')
            .trim()
            .toLowerCase();
    };

    const createResetButton = (control, relatedControls) => {
        const controlId = ensureControlId(control);
        const relatedIds = relatedControls
            .map((relatedControl) => ensureControlId(relatedControl))
            .filter(Boolean);
        const button = document.createElement('button');
        const label = labelForControl(control);

        button.type = 'button';
        button.className = 'btn btn-outline-secondary admin-filter-reset';
        button.dataset.filterResetTarget = controlId;
        if (relatedIds.length) {
            button.dataset.filterResetRelated = relatedIds.join(' ');
        }
        button.setAttribute('aria-label', `Svuota ${label}`);
        button.setAttribute('title', `Svuota ${label}`);
        button.innerHTML = '<i class="bi bi-x-lg" aria-hidden="true"></i>';

        return button;
    };

    const shouldEnhanceClearableControl = (control, form) => {
        if (!(control instanceof HTMLInputElement)) {
            return false;
        }

        if (
            control.disabled
            || control.readOnly
            || !control.classList.contains('form-control')
            || !CLEARABLE_FILTER_INPUT_TYPES.has(control.type)
            || control.matches('[data-filter-reset="off"]')
            || control.closest('[data-admin-nav-search]')
            || control.closest(`[data-filter-reset-wrapper], ${FILTER_RESET_SELECTOR}`)
        ) {
            return false;
        }

        const controlId = ensureControlId(control);
        if (form.querySelector(`${FILTER_RESET_SELECTOR}[data-filter-reset-target="${cssEscape(controlId)}"]`)) {
            return false;
        }

        return Boolean(control.name || relatedResetControlsFor(control, form).length);
    };

    const insertResetButton = (control, button) => {
        const existingGroup = control.closest('.input-group');

        if (existingGroup) {
            control.insertAdjacentElement('afterend', button);
            return;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'input-group';
        wrapper.dataset.filterResetWrapper = '1';

        control.insertAdjacentElement('beforebegin', wrapper);
        wrapper.appendChild(control);
        wrapper.appendChild(button);
    };

    const enhanceClearableFilterForm = (form) => {
        if (
            form.dataset.clearableFiltersInitialized === '1'
            || form.matches('[data-users-filter-form]')
            || form.dataset.filterReset === 'off'
            || !isGetForm(form)
        ) {
            return;
        }

        form.dataset.clearableFiltersInitialized = '1';

        Array.from(form.querySelectorAll('input.form-control')).forEach((control) => {
            if (!shouldEnhanceClearableControl(control, form)) {
                return;
            }

            const relatedControls = relatedResetControlsFor(control, form);
            const resetButton = createResetButton(control, relatedControls);

            insertResetButton(control, resetButton);

            const driverSearch = control.closest('[data-driver-search]');
            if (driverSearch) {
                driverSearch.classList.add('driver-filter-field--clearable');
            }
        });
    };

    const initFilterResetButton = (button) => {
        if (
            button.dataset.filterResetInitialized === '1'
            || button.closest('[data-users-filter-form]')
        ) {
            return;
        }

        button.dataset.filterResetInitialized = '1';

        const form = button.form || button.closest('form');
        const syncButton = () => updateResetButton(button);

        button.addEventListener('click', (event) => {
            event.preventDefault();

            const controls = controlsForResetButton(button);
            controls.forEach(clearResetControl);
            syncButton();

            const firstVisibleControl = controls.find((control) => control.type !== 'hidden');
            if (firstVisibleControl && typeof firstVisibleControl.focus === 'function') {
                firstVisibleControl.focus({ preventScroll: true });
            }

            if (!form || !isGetForm(form) || form.dataset.autoFilter === 'off') {
                return;
            }

            if (form.dataset.autoFilterInitialized === '1') {
                form.dispatchEvent(new CustomEvent('admin:filter-reset'));
                return;
            }

            if (hasFilterAction(form) || form.dataset.autoFilter === 'on') {
                submitFilterForm(form);
            }
        });

        controlsForResetButton(button).forEach((control) => {
            control.addEventListener('input', syncButton);
            control.addEventListener('change', syncButton);
        });

        syncButton();
    };

    const initFilterResetControls = (form) => {
        enhanceClearableFilterForm(form);
        form.querySelectorAll(FILTER_RESET_SELECTOR).forEach(initFilterResetButton);
    };

    const initAutoFilterForm = (form) => {
        if (
            form.dataset.autoFilterInitialized === '1'
            || form.dataset.autoFilter === 'off'
            || !isGetForm(form)
            || (form.dataset.autoFilter !== 'on' && !hasFilterSubmit(form))
        ) {
            return;
        }

        form.dataset.autoFilterInitialized = '1';

        let timeoutId = null;

        const scheduleSubmit = (delay = 0) => {
            window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => submitFilterForm(form), delay);
        };

        form.addEventListener('input', (event) => {
            const control = event.target;

            if (
                !isSupportedControl(control)
                || isSuggesterControl(control)
                || !isTextFilterControl(control)
                || !shouldSubmitWhileTyping(control)
            ) {
                return;
            }

            scheduleSubmit(textFilterDelayFor(control, form));
        });

        form.addEventListener('change', (event) => {
            const control = event.target;

            if (!isSupportedControl(control)) {
                return;
            }

            if (isSuggesterControl(control) && !hasSelectedSuggesterValue(control)) {
                return;
            }

            scheduleSubmit();
        });

        form.addEventListener('click', (event) => {
            if (!isOptionTrigger(event.target)) {
                return;
            }

            scheduleSubmit();
        });

        form.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') {
                return;
            }

            window.setTimeout(() => {
                if (event.defaultPrevented && isComboboxTrigger(event.target)) {
                    scheduleSubmit();
                }
            }, 0);
        });

        form.addEventListener('admin:filter-reset', () => {
            scheduleSubmit();
        });

        form.addEventListener('invalid', () => {
            form.dataset.autoFilterSubmitting = '0';
            form.removeAttribute('aria-busy');
        }, true);

        form.addEventListener('submit', (event) => {
            if (event.defaultPrevented) {
                return;
            }

            if (isSuggesterControl(document.activeElement) && !hasSelectedSuggesterValue(document.activeElement)) {
                event.preventDefault();
                window.clearTimeout(timeoutId);
                form.dataset.autoFilterSubmitting = '0';
                form.removeAttribute('aria-busy');
                return;
            }

            window.clearTimeout(timeoutId);
            form.dataset.autoFilterSubmitting = '1';
            form.setAttribute('aria-busy', 'true');
        });
    };

    const initDriverSearchField = (container) => {
        if (container.dataset.driverSearchInitialized === '1') {
            return;
        }

        const searchInput = container.querySelector('[data-driver-search-input]');
        const hiddenInput = container.querySelector('[data-driver-search-hidden]');
        const suggestionsList = container.querySelector('[data-driver-search-suggestions]');

        if (!searchInput || !hiddenInput || !suggestionsList) {
            return;
        }

        container.dataset.driverSearchInitialized = '1';

        const options = Array.from(suggestionsList.querySelectorAll('[data-driver-option]'));
        const emptyState = suggestionsList.querySelector('[data-driver-empty]');
        const maxVisibleOptions = 8;
        let activeIndex = -1;

        const normalize = (value) => String(value || '').trim().toLowerCase();
        const optionValue = (option) => option.dataset.value || '';
        const visibleOptions = () => options.filter((option) => !option.hidden);

        const clearActiveOption = () => {
            activeIndex = -1;
            searchInput.removeAttribute('aria-activedescendant');
            options.forEach((option) => {
                option.classList.remove('is-active');
                option.setAttribute('aria-selected', 'false');
            });
        };

        const setExpanded = (expanded) => {
            suggestionsList.hidden = !expanded;
            searchInput.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        };

        const setActiveOption = (index) => {
            const currentOptions = visibleOptions();

            clearActiveOption();

            if (!currentOptions.length) {
                return;
            }

            activeIndex = (index + currentOptions.length) % currentOptions.length;
            const activeOption = currentOptions[activeIndex];
            activeOption.classList.add('is-active');
            activeOption.setAttribute('aria-selected', 'true');
            searchInput.setAttribute('aria-activedescendant', activeOption.id);
            activeOption.scrollIntoView({ block: 'nearest' });
        };

        const resolveOption = (value) => {
            const normalizedValue = normalize(value);
            const numericMatch = normalizedValue.match(/^#?(\d+)\s*$/);

            if (numericMatch) {
                return options.find((option) => option.dataset.id === numericMatch[1]) || null;
            }

            return options.find((option) => normalize(optionValue(option)) === normalizedValue) || null;
        };

        const syncHiddenId = () => {
            hiddenInput.value = resolveOption(searchInput.value)?.dataset.id || '';
        };

        const renderSuggestions = () => {
            const query = normalize(searchInput.value);
            let shownOptions = 0;

            options.forEach((option) => {
                const searchable = normalize(`${option.dataset.search || ''} ${optionValue(option)}`);
                const matches = query === '' || searchable.includes(query);
                const shouldShow = matches && shownOptions < maxVisibleOptions;

                option.hidden = !shouldShow;

                if (shouldShow) {
                    shownOptions += 1;
                }
            });

            if (emptyState) {
                emptyState.hidden = shownOptions > 0;
            }

            clearActiveOption();
            setExpanded(document.activeElement === searchInput && (shownOptions > 0 || Boolean(emptyState && !emptyState.hidden)));
        };

        const selectOption = (option) => {
            if (!option) {
                return;
            }

            searchInput.value = optionValue(option);
            hiddenInput.value = option.dataset.id || '';
            clearActiveOption();
            setExpanded(false);
        };

        options.forEach((option) => {
            option.addEventListener('click', () => selectOption(option));
        });

        suggestionsList.addEventListener('mousedown', (event) => {
            event.preventDefault();
        });

        searchInput.addEventListener('focus', renderSuggestions);
        searchInput.addEventListener('input', () => {
            syncHiddenId();
            renderSuggestions();
        });
        searchInput.addEventListener('change', syncHiddenId);
        searchInput.addEventListener('blur', () => {
            syncHiddenId();
            window.setTimeout(() => setExpanded(false), 100);
        });
        searchInput.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                setExpanded(false);
                return;
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();

                if (suggestionsList.hidden) {
                    renderSuggestions();
                }

                setActiveOption(activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
                return;
            }

            if (event.key === 'Enter' && !suggestionsList.hidden) {
                const currentOptions = visibleOptions();
                const selectedOption = activeIndex >= 0
                    ? currentOptions[activeIndex]
                    : (currentOptions.length === 1 ? currentOptions[0] : null);

                if (selectedOption) {
                    event.preventDefault();
                    selectOption(selectedOption);
                }
            }
        });

        document.addEventListener('click', (event) => {
            if (!container.contains(event.target)) {
                setExpanded(false);
            }
        });

        syncHiddenId();
    };

    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('form').forEach(initFilterResetControls);
        document.querySelectorAll('[data-driver-search]').forEach(initDriverSearchField);
        document.querySelectorAll('form').forEach(initAutoFilterForm);
    });
})();
